<script>
import InfoIcon from '@/V2/components/InfoIcon.vue';
import DocumentCard, {
  DocumentCardSizes,
} from '@/V2/components/documents/DocumentCard.vue';
import {
  iIcon,
  iButton,
  iButtonTypes,
  iButtonStyles,
  iButtonSizes,
} from '@inscribe/signal';
import StatusBadge from '@/V2/components/status/StatusBadge.vue';
import {
  mapRunToStatus,
  mapRunToLabel,
} from '@/V2/documents/document-view/document-review-agent/utils';
import { RUN_STATUS } from '@/constants/analyst-constants';

export default {
  components: {
    InfoIcon,
    DocumentCard,
    iIcon,
    iButton,
    StatusBadge,
  },
  props: {
    title: {
      type: String,
    },
    description: {
      type: String,
    },
    infoIconLinkExternalUrl: {
      type: String,
    },
    data: {
      type: Array,
    },
    type: {
      type: String,
      default: 'documents',
    },
  },
  data() {
    return { DocumentCardSizes, iButtonTypes, iButtonStyles, iButtonSizes };
  },
  computed: {
    returnToPath() {
      return this.$route.fullPath;
    },
  },
  methods: {
    hideDrawer() {
      this.$emit('hide-drawer');
    },
    viewAllDocuments() {
      this.$emit('hide-drawer');
      this.$nextTick(() => {
        this.$router
          .push({
            name: 'customer-overview  ',
            params: {
              customerId: this.customerId,
            },
          })
          .catch((err) => {});
      });
    },
    getCustomerStatus(customer) {
      return mapRunToStatus({
        status: RUN_STATUS.FINISHED,
        output: {
          risk_level: customer.risk_level,
        },
      });
    },
    getCustomerStatusLabel(customer) {
      return mapRunToLabel({
        status: RUN_STATUS.FINISHED,
        output: {
          risk_level: customer.risk_level,
        },
      });
    },
    viewCustomer(customer, newTab) {
      if (newTab) {
        const baseUrl = window.location.href.substring(
          0,
          window.location.href.indexOf('#'),
        );
        window.open(`${baseUrl}#/customers/${customer.uuid}`, '_blank');
      } else {
        this.$emit('hide-drawer');
        this.$nextTick(() => {
          this.$router.push({
            name: 'customer-overview',
            params: { customerId: customer.id },
          });
        });
      }
    },
    viewDocument(document, newTab) {
      let customerId =
        document.customer_uuid ||
        document.customer_id ||
        this.$route.params.customer_id; // TODO: remove this once we have the customer_id in the document
      if (newTab) {
        const baseUrl = window.location.href.substring(
          0,
          window.location.href.indexOf('#'),
        );
        window.open(
          `${baseUrl}#/customer/${customerId}/document/${document.id}/signals`,
          '_blank',
        );
      } else {
        this.$emit('hide-drawer');
        this.$nextTick(() => {
          this.$router.push({
            name: 'document-signals',
            params: {
              customer_id: customerId,
              document_id: document.id,
            },
            query: {
              return_to: this.returnToPath,
            },
          });
        });
      }
    },
  },
};
</script>

<template>
  <div
    class="signal-documents-block d-flex flex-column i-radius-xl"
    data-testid="customer-fraud-signal-data-block"
  >
    <!-- title -->
    <div
      class="title d-flex justify-content-between align-items-center i-px-8 i-py-6 i-gap-4"
    >
      <div class="signal-name i-text-button-3 i-text-neutral-120">
        {{ title }}
      </div>
      <info-icon
        class="i-mr-4"
        :title="title"
        :description="description"
        :link-text="infoIconLinkExternalUrl ? 'Read more' : null"
        :link-external-url="infoIconLinkExternalUrl"
      />
    </div>

    <!-- data -->
    <div class="data-list d-flex flex-column">
      <!-- documents -->
      <template v-if="type === 'documents'">
        <document-card
          v-for="document in data"
          :key="document.id"
          :size="DocumentCardSizes.SMALL"
          :limited-width="230"
          right-icon="medium-link-square-1"
          :document="document"
          :return-to="returnToPath"
          @on-right-icon-click="viewDocument(document, true)"
          @on-document-click="viewDocument(document, false)"
          data-testid="customer-fraud-signal-document-card"
        />
      </template>

      <!-- customers -->
      <template v-if="type === 'customers'">
        <div
          class="customer-card d-flex align-items-center i-p-8 i-gap-8"
          v-for="customer in data"
          :key="customer.id"
          @click="viewCustomer(customer, false)"
        >
          <div class="icon-container i-p-5 i-radius-xl">
            <i-icon symbol="medium-user-single" :size="16"></i-icon>
          </div>
          <div
            class="customer-data d-flex flex-column i-text-button-3 i-text-neutral-120"
          >
            <div class="customer-name i-text-button-2 i-text-neutral-120">
              {{ customer.name }}
            </div>
            <div class="customer-detials i-text-body-2 i-text-neutral-110">
              {{ customer.document_count }}
              {{ customer.document_count === 1 ? 'Document' : 'Documents' }}
              •
              {{ customer.high_risk_document_count }}
              High Risk
            </div>
          </div>
          <status-badge
            :state="getCustomerStatus(customer)"
            :label="getCustomerStatusLabel(customer)"
          />
          <i-button
            left-icon="medium-link-square-1"
            :type="iButtonTypes.NEUTRAL"
            :button-style="iButtonStyles.GHOST"
            :size="iButtonSizes.SMALL"
            class="i-ml-auto"
            @button-click="viewCustomer(customer, true)"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<style lang="scss">
.signal-documents-block {
  border: 1px solid $light-neutral-60;
  .title {
    border-top-left-radius: 12px;
    border-top-right-radius: 12px;
    border-bottom: 1px solid $light-neutral-60;
    user-select: none;
  }

  .data-list {
    border-bottom-left-radius: 12px;
    border-bottom-right-radius: 12px;

    .document-card,
    .customer-card {
      border: 0;
      &:not(:last-of-type) {
        border-bottom: 1px solid $light-neutral-60;
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
      }
    }

    .customer-card {
      cursor: pointer;
      &:hover {
        background-color: $light-neutral-20;
      }

      .icon-container {
        border: 1px solid $light-neutral-60;
      }

      .customer-data {
        flex: 1;
      }
    }
  }
}
</style>
